import { useQuery } from '@tanstack/react-query'
import { DetailSection, QueryBlock } from '../../core/index.js'
import type { MessagesAdminApi } from '../adminApi.js'
import type { FacilityDeliveryView } from '../types.js'

/** "9:00 PM to 7:00 AM" from "21:00" and "07:00". */
function clock(time: string): string {
  const [hours, minutes] = time.split(':').map(Number)
  const date = new Date(Date.UTC(2026, 0, 1, hours ?? 0, minutes ?? 0))
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })
}

/** The admin's read-only view of a facility's rules over texts: the switch, quiet hours and which kinds of message are texted, by group. */
export function AdminFacilityDeliverySection({ api, clientId, facilityId }: { api: Pick<MessagesAdminApi, 'facilityDelivery'>; clientId: string; facilityId: string }) {
  const query = useQuery({ queryKey: ['admin', 'facility', clientId, facilityId, 'delivery'], queryFn: () => api.facilityDelivery(clientId, facilityId) })
  return (
    <DetailSection title="Notifications" endpoint="…/delivery">
      <QueryBlock query={query} missing="No rules.">
        {(view: FacilityDeliveryView) => (
          <div className="flex flex-col gap-2 text-[11px]">
            <div className="text-ink">
              <span className="font-bold">Texts</span> · {view.sms.offered ? (view.sms.enabled ? 'on at this facility' : 'switched off at this facility') : 'not offered on this deployment'}
              {view.sms.quietHours && (
                <span className="text-ink-mid">
                  {' '}
                  · quiet hours {clock(view.sms.quietHours.start)} to {clock(view.sms.quietHours.end)} ({view.sms.timezone})
                </span>
              )}
            </div>
            {view.sms.groups.map((group) => (
              <div key={group.key}>
                <div className="font-bold text-ink">{group.label}</div>
                <ul className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-ink-mid">
                  {group.types.map((entry) => (
                    <li key={entry.type} className={entry.texted ? 'text-green' : 'text-ink-mid'}>
                      {entry.type.replace(/_/g, ' ')} · {entry.texted ? 'texted' : 'in app only'}
                      {entry.texted !== entry.default && <span className="text-gold"> (changed)</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </QueryBlock>
    </DetailSection>
  )
}
